import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Building2, Landmark, ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_creditai-africa/artifacts/q9fyvy5m_ChatGPT%20Image%20Feb%201%2C%202026%2C%2012_34_17%20PM.png";

const ROLES = [
  { id: 'COMPANY', label: 'Company', icon: Building2, description: 'I\'m seeking funding for my business', color: 'blue' },
  { id: 'FUNDER', label: 'Funder', icon: Landmark, description: 'I\'m a bank, DFI, or fund looking to invest', color: 'emerald' },
];

export default function AuthPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const getInitialMode = () => {
    if (location.pathname === '/register') return 'register';
    if (location.pathname === '/login') return 'login';
    return searchParams.get('mode') || 'login';
  };
  
  const getInitialRole = () => {
    const roleParam = searchParams.get('role');
    if (roleParam === 'company') return 'COMPANY';
    if (roleParam === 'funder') return 'FUNDER';
    return null;
  };
  
  const [mode, setMode] = useState(getInitialMode());
  
  useEffect(() => {
    if (location.pathname === '/register') setMode('register');
    else if (location.pathname === '/login') setMode('login');
  }, [location.pathname]);
  
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(getInitialRole());
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    confirmPassword: '',
  });
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        const user = await login(formData.email, formData.password);
        toast.success(`Welcome back, ${user.full_name}!`);
        navigateToDashboard(user.role);
      } else {
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        if (!selectedRole) {
          toast.error('Please select your account type');
          setLoading(false);
          return;
        }
        const user = await register({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          role: selectedRole,
        });
        toast.success('Account created successfully!');
        navigateToOnboarding(user.role);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const navigateToOnboarding = (role) => {
    switch (role) {
      case 'COMPANY':
        navigate('/company/onboarding');
        break;
      case 'FUNDER':
        navigate('/funder/onboarding');
        break;
      case 'ADMIN':
        navigate('/admin');
        break;
      default:
        navigate('/');
    }
  };

  const navigateToDashboard = (role) => {
    switch (role) {
      case 'COMPANY':
        navigate('/company');
        break;
      case 'FUNDER':
        navigate('/funder');
        break;
      case 'ADMIN':
        navigate('/admin');
        break;
      default:
        navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <img src={LOGO_URL} alt="Apaka" className="h-16" data-testid="auth-logo" />
        </Link>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-heading text-slate-900" data-testid="auth-title">
              {mode === 'login' ? 'Welcome Back' : 'Create Account'}
            </CardTitle>
            <CardDescription className="text-slate-500">
              {mode === 'login' 
                ? 'Sign in to access your dashboard' 
                : 'Join Apaka to transform your credit decisions'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <>
                  {/* Only show role selection if not pre-selected from URL */}
                  {!getInitialRole() && (
                    <div className="space-y-2">
                      <Label className="text-slate-700">Account Type</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {ROLES.map((role) => (
                          <button
                            key={role.id}
                            type="button"
                            onClick={() => setSelectedRole(role.id)}
                            className={`p-4 rounded-lg border-2 transition-all text-left ${
                              selectedRole === role.id
                                ? role.color === 'blue' 
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-emerald-500 bg-emerald-50'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                            data-testid={`role-${role.id.toLowerCase()}`}
                          >
                            <role.icon className={`h-6 w-6 mb-2 ${
                              selectedRole === role.id 
                                ? role.color === 'blue' ? 'text-blue-600' : 'text-emerald-600'
                                : 'text-slate-400'
                            }`} />
                            <div className={`font-medium ${
                              selectedRole === role.id ? 'text-slate-900' : 'text-slate-700'
                            }`}>{role.label}</div>
                            <div className="text-xs text-slate-500 mt-1">{role.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Show selected role badge when pre-selected from URL */}
                  {getInitialRole() && (
                    <div className={`p-4 rounded-lg border-2 ${
                      selectedRole === 'COMPANY' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-emerald-500 bg-emerald-50'
                    }`}>
                      <div className="flex items-center gap-3">
                        {selectedRole === 'COMPANY' ? (
                          <Building2 className="h-6 w-6 text-blue-600" />
                        ) : (
                          <Landmark className="h-6 w-6 text-emerald-600" />
                        )}
                        <div>
                          <div className="font-medium text-slate-900">
                            {selectedRole === 'COMPANY' ? 'Company Account' : 'Funder Account'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {selectedRole === 'COMPANY' 
                              ? 'Seeking funding for your business' 
                              : 'Bank, DFI, or fund looking to invest'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-slate-700">Full Name</Label>
                    <Input
                      id="full_name"
                      placeholder="John Doe"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      className="border-slate-200"
                      data-testid="input-fullname"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Company Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="border-slate-200"
                  data-testid="input-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="border-slate-200"
                  data-testid="input-password"
                />
              </div>

              {mode === 'register' && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    className="border-slate-200"
                    data-testid="input-confirm-password"
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className={`w-full ${
                  selectedRole === 'FUNDER' 
                    ? 'bg-emerald-600 hover:bg-emerald-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                data-testid="auth-submit-btn"
              >
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {mode === 'login' ? 'Sign In' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-600 text-sm">
                {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
                <button
                  onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                  className="ml-2 text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="auth-toggle-mode"
                >
                  {mode === 'login' ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </div>

            <Link to="/" className="flex items-center justify-center mt-6 text-slate-500 hover:text-slate-700 text-sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
